import React, { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase/config';
import {
    FaUser, FaChevronDown, FaChevronRight, FaUsers, FaSpinner,
    FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaSearch,
    FaExpand, FaCompress, FaDownload, FaNetworkWired, FaTimes
} from 'react-icons/fa';

const ReferralTree = () => {
    const [users, setUsers] = useState([]);
    const [expandedUsers, setExpandedUsers] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [expandAll, setExpandAll] = useState(false);

    useEffect(() => {
        const usersRef = ref(db, 'HTAMS/users');
        const unsubscribe = onValue(usersRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const usersList = Object.entries(data)
                    .filter(([uid, userData]) => {
                        const role = userData.role?.toLowerCase() || '';
                        return role === 'agency' || role === 'mega agency' || role === 'diamond agency';
                    })
                    .map(([uid, userData]) => ({
                        uid,
                        name: userData.name || 'Unknown',
                        email: userData.email || 'N/A',
                        phone: userData.phone || userData.mobile || 'N/A',
                        referredBy: userData.referredBy || null,
                        city: userData.city || 'N/A',
                        state: userData.state || 'N/A',
                        level: userData.level || 'N/A',
                        createdAt: userData.createdAt || Date.now(),
                        role: userData.role || 'agency',
                    }));
                setUsers(usersList);
            } else {
                setUsers([]);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const buildReferralTree = () => {
        const userMap = {};
        const rootUsers = [];
        users.forEach(user => { userMap[user.uid] = { ...user, referrals: [] }; });
        users.forEach(user => {
            if (user.referredBy && userMap[user.referredBy]) {
                userMap[user.referredBy].referrals.push(userMap[user.uid]);
            } else {
                rootUsers.push(userMap[user.uid]);
            }
        });
        return rootUsers;
    };

    const filterUsers = (usersList) => {
        if (!searchTerm.trim()) return usersList;
        const searchLower = searchTerm.toLowerCase();
        const filterRecursive = (user) => {
            const matchesSearch =
                user.name.toLowerCase().includes(searchLower) ||
                user.email.toLowerCase().includes(searchLower) ||
                user.phone.includes(searchTerm) ||
                user.uid.toLowerCase().includes(searchLower);
            const filteredReferrals = user.referrals.map(filterRecursive).filter(Boolean);
            if (matchesSearch || filteredReferrals.length > 0) {
                return { ...user, referrals: filteredReferrals };
            }
            return null;
        };
        return usersList.map(filterRecursive).filter(Boolean);
    };

    const toggleUser = (uid) => {
        const newExpanded = new Set(expandedUsers);
        if (newExpanded.has(uid)) newExpanded.delete(uid);
        else newExpanded.add(uid);
        setExpandedUsers(newExpanded);
    };

    const toggleExpandAll = () => {
        if (expandAll) {
            setExpandedUsers(new Set());
        } else {
            const allUids = new Set();
            const addAllUids = (usersList) => {
                usersList.forEach(user => {
                    allUids.add(user.uid);
                    if (user.referrals.length > 0) addAllUids(user.referrals);
                });
            };
            addAllUids(buildReferralTree());
            setExpandedUsers(allUids);
        }
        setExpandAll(!expandAll);
    };

    const countTotalReferrals = (user) => {
        let count = user.referrals.length;
        user.referrals.forEach(referral => { count += countTotalReferrals(referral); });
        return count;
    };

    const exportToCSV = () => {
        const rows = [];
        const addUserToRows = (user, level = 0) => {
            rows.push([
                '  '.repeat(level) + user.name,
                user.email,
                user.phone,
                user.city,
                user.state,
                user.level,
                user.role,
                user.referrals.length,
                countTotalReferrals(user),
                new Date(user.createdAt).toLocaleDateString()
            ]);
            user.referrals.forEach(referral => addUserToRows(referral, level + 1));
        };
        const tree = buildReferralTree();
        tree.forEach(user => addUserToRows(user));
        const headers = ['Name', 'Email', 'Phone', 'City', 'State', 'Level', 'Role', 'Direct Referrals', 'Total Referrals', 'Joined Date'];
        const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `agency-referral-tree-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const renderUserNode = (user, level = 0, isLast = false, parentLines = []) => {
        const isExpanded = expandedUsers.has(user.uid);
        const hasReferrals = user.referrals.length > 0;

        const roleLower = user.role?.toLowerCase() || '';
        let roleClass = 'role-gray';
        if (roleLower.includes('mega')) roleClass = 'role-purple';
        else if (roleLower.includes('diamond')) roleClass = 'role-blue';

        return (
            <div key={user.uid} className="tree-branch">
                <div
                    className={`tree-row ${selectedUser?.uid === user.uid ? 'selected' : ''}`}
                    onClick={() => setSelectedUser(user)}
                >
                    {/* Indentation & Lines */}
                    <div className="indent-container">
                        {parentLines.map((line, idx) => (
                            <div key={idx} className={`indent-line ${line ? 'vertical' : ''}`}></div>
                        ))}
                        <div className={`indent-line ${isLast ? 'corner' : 'vertical-horizontal'}`}></div>
                    </div>

                    {/* Node Content */}
                    <div className="node-content">
                        <span
                            className={`toggle-icon ${!hasReferrals ? 'invisible' : ''}`}
                            onClick={(e) => { e.stopPropagation(); toggleUser(user.uid); }}
                        >
                            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </span>

                        <div className={`avatar-circle ${roleClass}`}>
                            {user.name.charAt(0).toUpperCase()}
                        </div>

                        <div className="node-text">
                            <div className="name-row">
                                <span className="name">{user.name}</span>
                                <span className={`role-pill ${roleClass}`}>{user.role}</span>
                            </div>
                            <div className="meta-row">
                                <span className="id-tag">ID: {user.uid.slice(-6).toUpperCase()}</span>
                                {hasReferrals && (
                                    <span className="count-badge" title="Total Network Size">
                                        <FaUsers /> {countTotalReferrals(user)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {isExpanded && hasReferrals && (
                    <div className="children-container">
                        {user.referrals.map((referral, index) => (
                            renderUserNode(
                                referral,
                                level + 1,
                                index === user.referrals.length - 1,
                                [...parentLines, !isLast]
                            )
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const referralTree = filterUsers(buildReferralTree());

    return (
        <div className="page-container">
            {/* Minimal Header */}
            <div className="header-card">
                <div className="header-left">
                    <h1><FaNetworkWired /> Referral Network</h1>
                    <span className="header-stats">{users.length} Agencies &bull; {referralTree.length} Roots</span>
                </div>
                <div className="header-right">
                    <div className="search-wrap">
                        <FaSearch />
                        <input
                            placeholder="Find agency..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="tool-btn" onClick={toggleExpandAll} title={expandAll ? "Collapse All" : "Expand All"}>
                        {expandAll ? <FaCompress /> : <FaExpand />}
                    </button>
                    <button className="tool-btn primary" onClick={exportToCSV} title="Export CSV">
                        <FaDownload /> CSV
                    </button>
                </div>
            </div>

            <div className="main-grid">
                {/* Tree Section */}
                <div className="tree-panel">
                    {loading ? (
                        <div className="state-msg"><FaSpinner className="spin" /> Loading...</div>
                    ) : referralTree.length > 0 ? (
                        <div className="tree-root">
                            {referralTree.map((user, i) => renderUserNode(user, 0, i === referralTree.length - 1, []))}
                        </div>
                    ) : (
                        <div className="state-msg">No agencies found.</div>
                    )}
                </div>

                {/* Details Side Panel */}
                {selectedUser ? (
                    <div className="details-panel">
                        <div className="details-header">
                            <h3>Agency Profile</h3>
                            <button onClick={() => setSelectedUser(null)}><FaTimes /></button>
                        </div>
                        <div className="details-body">
                            <div className="profile-hero">
                                <div className={`big-avatar ${selectedUser.role.toLowerCase().includes('mega') ? 'purple' : selectedUser.role.toLowerCase().includes('diamond') ? 'blue' : 'gray'}`}>
                                    {selectedUser.name.charAt(0).toUpperCase()}
                                </div>
                                <h2>{selectedUser.name}</h2>
                                <span className="full-role">{selectedUser.role}</span>
                            </div>

                            <div className="stats-row">
                                <div className="stat-box">
                                    <label>Direct</label>
                                    <b>{selectedUser.referrals.length}</b>
                                </div>
                                <div className="stat-box">
                                    <label>Network</label>
                                    <b>{countTotalReferrals(selectedUser)}</b>
                                </div>
                            </div>

                            <hr className="divider" />

                            <div className="info-list">
                                <div className="info-item">
                                    <FaPhone className="icon" />
                                    <span>{selectedUser.phone}</span>
                                </div>
                                <div className="info-item">
                                    <FaEnvelope className="icon" />
                                    <span>{selectedUser.email}</span>
                                </div>
                                <div className="info-item">
                                    <FaMapMarkerAlt className="icon" />
                                    <span>{selectedUser.city}, {selectedUser.state}</span>
                                </div>
                                <div className="info-item">
                                    <FaCalendarAlt className="icon" />
                                    <span>Joined {new Date(selectedUser.createdAt).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="details-placeholder">
                        <FaNetworkWired />
                        <p>Select an agency to view details</p>
                    </div>
                )}
            </div>

            <style jsx>{`
                .page-container { padding: 16px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; color: #334155; }
                
                /* Header */
                .header-card { 
                    background: white; border-radius: 10px; padding: 12px 16px; 
                    display: flex; justify-content: space-between; align-items: center; 
                    border: 1px solid #e2e8f0; margin-bottom: 16px; 
                    box-shadow: 0 1px 2px rgba(0,0,0,0.03); flex-wrap: wrap; gap: 12px;
                }
                .header-left h1 { margin: 0; font-size: 1.1rem; font-weight: 700; color: #1e293b; display: flex; align-items: center; gap: 8px; }
                .header-left h1 svg { color: #3b82f6; }
                .header-stats { font-size: 0.8rem; color: #64748b; margin-left: 4px; }
                
                .header-right { display: flex; gap: 8px; align-items: center; }
                .search-wrap { 
                    background: #f1f5f9; border-radius: 6px; padding: 6px 10px; 
                    display: flex; align-items: center; gap: 8px; width: 220px; border: 1px solid transparent; 
                }
                .search-wrap:focus-within { border-color: #3b82f6; background: white; }
                .search-wrap input { border: none; background: transparent; outline: none; font-size: 0.85rem; width: 100%; color: #334155; }
                .search-wrap svg { color: #94a3b8; font-size: 0.8rem; }
                
                .tool-btn { 
                    padding: 6px 12px; border: 1px solid #e2e8f0; background: white; 
                    border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer; color: #475569; 
                    display: flex; align-items: center; gap: 6px; 
                }
                .tool-btn.primary { background: #3b82f6; color: white; border-color: #3b82f6; }
                .tool-btn:hover { opacity: 0.9; }

                /* Main Grid */
                .main-grid { display: grid; grid-template-columns: 1fr 300px; gap: 16px; align-items: start; height: calc(100vh - 100px); }
                
                /* Tree Panel */
                .tree-panel { 
                    background: white; border-radius: 10px; border: 1px solid #e2e8f0; 
                    overflow-y: auto; height: 100%; padding: 12px; 
                    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
                }
                
                /* Tree Lines Logic */
                .tree-root { display: flex; flex-direction: column; }
                .tree-branch { display: flex; flex-direction: column; }
                
                .tree-row { 
                    display: flex; align-items: center; height: 36px; 
                    border-radius: 4px; cursor: pointer; transition: background 0.1s; 
                    position: relative; white-space: nowrap;
                }
                .tree-row:hover { background: #f1f5f9; }
                .tree-row.selected { background: #eff6ff; }
                
                .indent-container { display: flex; height: 100%; }
                .indent-line { width: 24px; height: 100%; position: relative; flex-shrink: 0; }
                
                /* Vertical line for connecting deep levels */
                .indent-line.vertical::before {
                    content: ''; position: absolute; left: 50%; top: 0; bottom: 0; border-left: 1px solid #e2e8f0;
                }
                
                /* L-shape for last child */
                .indent-line.corner::before {
                    content: ''; position: absolute; left: 50%; top: 0; height: 50%; border-left: 1px solid #e2e8f0;
                }
                .indent-line.corner::after {
                    content: ''; position: absolute; left: 50%; top: 50%; right: 0; border-top: 1px solid #e2e8f0;
                }
                
                /* T-shape for middle children */
                .indent-line.vertical-horizontal::before {
                    content: ''; position: absolute; left: 50%; top: 0; bottom: 0; border-left: 1px solid #e2e8f0;
                }
                .indent-line.vertical-horizontal::after {
                    content: ''; position: absolute; left: 50%; top: 50%; right: 0; border-top: 1px solid #e2e8f0;
                }

                .node-content { display: flex; align-items: center; gap: 8px; padding-right: 8px; flex: 1; }
                
                .toggle-icon { width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 0.6rem; color: #94a3b8; cursor: pointer; border-radius: 4px; }
                .toggle-icon:hover { background: #e2e8f0; color: #475569; }
                .invisible { visibility: hidden; }

                .avatar-circle { 
                    width: 24px; height: 24px; border-radius: 50%; display: flex; 
                    align-items: center; justify-content: center; font-size: 0.7rem; 
                    font-weight: 700; color: white; flex-shrink: 0; 
                }
                .role-purple { background: linear-gradient(135deg, #a855f7, #6b21a8); }
                .role-blue { background: linear-gradient(135deg, #3b82f6, #1e40af); }
                .role-gray { background: #94a3b8; }

                .node-text { display: flex; align-items: center; justify-content: space-between; flex: 1; gap: 12px; }
                .name-row { display: flex; align-items: center; gap: 8px; }
                .name { font-size: 0.85rem; font-weight: 500; color: #1e293b; }
                
                .role-pill { font-size: 0.6rem; padding: 1px 6px; border-radius: 10px; text-transform: uppercase; font-weight: 600; }
                .role-pill.role-purple { background: #f3e8ff; color: #7e22ce; }
                .role-pill.role-blue { background: #dbeafe; color: #1d4ed8; }
                .role-pill.role-gray { background: #f1f5f9; color: #64748b; }

                .meta-row { display: flex; align-items: center; gap: 8px; }
                .id-tag { font-family: monospace; font-size: 0.7rem; color: #94a3b8; }
                .count-badge { font-size: 0.7rem; color: #64748b; display: flex; align-items: center; gap: 4px; background: #f8fafc; padding: 1px 6px; border-radius: 8px; border: 1px solid #e2e8f0; }

                /* Details Panel */
                .details-panel { 
                    background: white; border-radius: 10px; border: 1px solid #e2e8f0; 
                    height: 100%; display: flex; flex-direction: column; overflow: hidden; 
                    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
                }
                .details-placeholder { 
                    background: white; border-radius: 10px; border: 1px solid #e2e8f0; border-style: dashed;
                    height: 100%; display: flex; flex-direction: column; align-items: center; 
                    justify-content: center; color: #cbd5e1; gap: 12px;
                }
                .details-placeholder svg { font-size: 3rem; }

                .details-header { padding: 12px 16px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
                .details-header h3 { margin: 0; font-size: 0.95rem; font-weight: 600; }
                .details-header button { border: none; background: none; color: #94a3b8; cursor: pointer; }

                .details-body { padding: 20px; overflow-y: auto; }
                .profile-hero { text-align: center; margin-bottom: 20px; }
                .big-avatar { width: 60px; height: 60px; border-radius: 50%; margin: 0 auto 12px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; font-weight: 700; color: white; }
                .big-avatar.purple { background: #a855f7; }
                .big-avatar.blue { background: #3b82f6; }
                .big-avatar.gray { background: #94a3b8; }
                
                .profile-hero h2 { margin: 0; font-size: 1.1rem; color: #1e293b; }
                .full-role { font-size: 0.75rem; color: #64748b; text-transform: uppercase; font-weight: 600; margin-top: 4px; display: block; }
                
                .stats-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 20px; }
                .stat-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; text-align: center; }
                .stat-box label { display: block; font-size: 0.65rem; text-transform: uppercase; color: #64748b; font-weight: 600; margin-bottom: 4px; }
                .stat-box b { font-size: 1.1rem; color: #0f172a; }

                .divider { border: 0; border-top: 1px solid #e2e8f0; margin: 0 0 20px 0; }
                
                .info-list { display: flex; flex-direction: column; gap: 14px; }
                .info-item { display: flex; align-items: center; gap: 10px; font-size: 0.85rem; color: #334155; }
                .info-item .icon { color: #94a3b8; width: 16px; }

                .state-msg { text-align: center; padding: 40px; color: #94a3b8; font-size: 0.9rem; }
                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { 100% { transform: rotate(360deg); } }

                /* Responsive */
                @media (max-width: 900px) {
                    .main-grid { grid-template-columns: 1fr; }
                    .details-panel { position: fixed; bottom: 0; left: 0; right: 0; top: auto; height: 60vh; border-radius: 16px 16px 0 0; z-index: 100; box-shadow: 0 -4px 20px rgba(0,0,0,0.1); }
                    .details-placeholder { display: none; }
                }
            `}</style>
        </div>
    );
};

export default ReferralTree;
